import React from 'react';
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  FileText,
  MapPin,
  Users
} from 'lucide-react';

const palette = {
  navy: '#0F2742',
  green: '#1F8A70',
  greenSoft: '#E8F5F2',
  gold: '#C8A45C',
  goldSoft: '#FFF7E8',
  red: '#B45353',
  redSoft: '#FBEAEA',
  border: '#DCE7E3',
  surface: '#F7F9FB',
  muted: '#6B7280'
};

const hasMojibake = (value = '') => /[ØÙأک™\uFFFD]/.test(String(value));

const safeValue = (value, fallback = 'غير متوفر') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return hasMojibake(value) ? fallback : value;
};

const normalizeStatus = (status) => {
  if (!status || hasMojibake(status)) return 'قادمة';
  const text = String(status).trim();
  if (text === 'upcoming' || text === 'future') return 'قادمة';
  if (text === 'completed' || text === 'done') return 'منعقدة';
  if (text === 'postponed') return 'مؤجلة';
  return text;
};

const hearingStatusStyles = {
  قادمة: {
    icon: AlertCircle,
    bg: palette.goldSoft,
    chipBg: palette.gold,
    chipColor: '#FFFFFF',
    iconBg: `linear-gradient(135deg, ${palette.gold} 0%, #D9BA78 100%)`
  },
  منعقدة: {
    icon: CheckCircle2,
    bg: '#F3F4F6',
    chipBg: '#7B8794',
    chipColor: '#FFFFFF',
    iconBg: 'linear-gradient(135deg, #7B8794 0%, #9AA6B2 100%)'
  },
  مؤجلة: {
    icon: AlertCircle,
    bg: palette.redSoft,
    chipBg: palette.red,
    chipColor: '#FFFFFF',
    iconBg: `linear-gradient(135deg, ${palette.red} 0%, #D16F6F 100%)`
  }
};

const HearingsSection = ({ caseData }) => {
  const hearings = Array.isArray(caseData?.hearings) ? caseData.hearings : [];

  return (
    <section
      style={{
        background: '#FFFFFF',
        border: `1px solid ${palette.border}`,
        borderRadius: 28,
        padding: 28,
        boxShadow: '0 20px 50px rgba(15, 39, 66, 0.06)'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          marginBottom: 24
        }}
      >
        <div
          style={{
            width: 50,
            height: 50,
            borderRadius: 18,
            background: `linear-gradient(135deg, ${palette.goldSoft} 0%, #FFFFFF 100%)`,
            border: `1px solid ${palette.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <CalendarDays size={24} color={palette.gold} strokeWidth={2.2} />
        </div>

        <div>
          <h2
            style={{
              margin: 0,
              fontSize: '1.55rem',
              fontWeight: 800,
              color: palette.navy
            }}
          >
            الجلسات
          </h2>
          <p
            style={{
              margin: '6px 0 0',
              color: palette.muted,
              fontSize: '0.95rem'
            }}
          >
            راجع مواعيد الجلسات، حالتها، وأبرز الملاحظات المتعلقة بها.
          </p>
        </div>
      </div>

      {hearings.length > 0 ? (
        <div style={{ display: 'grid', gap: 18 }}>
          {hearings.map((hearing) => {
            const status = normalizeStatus(hearing.status);
            const currentStyle = hearingStatusStyles[status] || hearingStatusStyles.قادمة;
            const StatusIcon = currentStyle.icon;

            return (
              <article
                key={safeValue(hearing.id, Math.random())}
                style={{
                  background: currentStyle.bg,
                  border: `1px solid ${palette.border}`,
                  borderRadius: 24,
                  padding: 22,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  boxShadow: '0 16px 34px rgba(15, 39, 66, 0.05)'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 16,
                    marginBottom: 18,
                    flexWrap: 'wrap'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 16,
                        background: currentStyle.iconBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#FFFFFF',
                        flexShrink: 0
                      }}
                    >
                      <StatusIcon size={22} strokeWidth={2.2} />
                    </div>

                    <div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: '1.12rem',
                          fontWeight: 800,
                          color: palette.navy
                        }}
                      >
                        الجلسة {safeValue(hearing.id, 'غير محدد')}
                      </h3>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '6px 12px',
                          borderRadius: 999,
                          background: currentStyle.chipBg,
                          color: currentStyle.chipColor,
                          fontSize: '0.82rem',
                          fontWeight: 700,
                          marginTop: 8
                        }}
                      >
                        {status}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                    gap: 14,
                    marginBottom: hearing.notes || hearing.attendees?.length ? 16 : 0
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                      padding: '14px 16px',
                      background: '#FFFFFF',
                      border: `1px solid ${palette.border}`,
                      borderRadius: 18
                    }}
                  >
                    <CalendarDays size={18} color={palette.green} strokeWidth={2.1} />
                    <div>
                      <div style={{ color: palette.muted, fontSize: '0.8rem', fontWeight: 700 }}>التاريخ</div>
                      <div style={{ color: palette.navy, fontSize: '0.95rem', fontWeight: 700 }}>
                        {safeValue(hearing.date)}
                      </div>
                      {hearing.day && (
                        <div style={{ color: palette.muted, fontSize: '0.82rem', marginTop: 2 }}>
                          {safeValue(hearing.day)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                      padding: '14px 16px',
                      background: '#FFFFFF',
                      border: `1px solid ${palette.border}`,
                      borderRadius: 18
                    }}
                  >
                    <Clock3 size={18} color={palette.green} strokeWidth={2.1} />
                    <div>
                      <div style={{ color: palette.muted, fontSize: '0.8rem', fontWeight: 700 }}>الوقت</div>
                      <div style={{ color: palette.navy, fontSize: '0.95rem', fontWeight: 700 }}>
                        {safeValue(hearing.time)}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                      padding: '14px 16px',
                      background: '#FFFFFF',
                      border: `1px solid ${palette.border}`,
                      borderRadius: 18
                    }}
                  >
                    <MapPin size={18} color={palette.green} strokeWidth={2.1} />
                    <div>
                      <div style={{ color: palette.muted, fontSize: '0.8rem', fontWeight: 700 }}>المكان</div>
                      <div style={{ color: palette.navy, fontSize: '0.95rem', fontWeight: 700 }}>
                        {safeValue(hearing.location)}
                      </div>
                    </div>
                  </div>
                </div>

                {Array.isArray(hearing.attendees) && hearing.attendees.length > 0 && (
                  <div
                    style={{
                      background: '#FFFFFF',
                      border: `1px solid ${palette.border}`,
                      borderRadius: 18,
                      padding: 16,
                      marginBottom: hearing.notes ? 14 : 0
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        color: palette.navy,
                        fontWeight: 700,
                        marginBottom: 10
                      }}
                    >
                      <Users size={18} color={palette.green} strokeWidth={2.1} />
                      الحضور
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {hearing.attendees.map((attendee, index) => (
                        <span
                          key={`${attendee}-${index}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '7px 12px',
                            borderRadius: 999,
                            background: palette.greenSoft,
                            color: palette.green,
                            fontSize: '0.82rem',
                            fontWeight: 700
                          }}
                        >
                          {safeValue(attendee, `الحاضر ${index + 1}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {hearing.notes && (
                  <div
                    style={{
                      background: '#FFFFFF',
                      border: `1px solid ${palette.border}`,
                      borderRadius: 18,
                      padding: 16
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        color: palette.navy,
                        fontWeight: 700,
                        marginBottom: 10
                      }}
                    >
                      <FileText size={18} color={palette.gold} strokeWidth={2.1} />
                      ملاحظات الجلسة
                    </div>
                    <p
                      style={{
                        margin: 0,
                        color: palette.muted,
                        lineHeight: 1.9,
                        fontSize: '0.95rem'
                      }}
                    >
                      {safeValue(hearing.notes)}
                    </p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            background: palette.surface,
            border: `1px dashed ${palette.border}`,
            borderRadius: 24,
            padding: 36,
            textAlign: 'center',
            color: palette.muted
          }}
        >
          لا توجد جلسات مجدولة حاليًا.
        </div>
      )}
    </section>
  );
};

export default HearingsSection;
